import React, { useEffect, useState } from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
    TextField,
    MenuItem,
    Button,
    Box,
    Typography,
} from "@mui/material";
import { X } from "lucide-react";
import apiClient from "../../services/apiClient";
import { DropdownField, LabeledTextField } from "./PetServiceChargeCodeFormPage";

interface ChargeCodeDialogProps {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
    initialData?: any;
}

const ChargeCodeDialog: React.FC<ChargeCodeDialogProps> = ({
    open,
    onClose,
    onSuccess,
    initialData,
}) => {
    const [chargeCode, setChargeCode] = useState("");
    const [serviceName, setServiceName] = useState("");
    const [serviceType, setServiceType] = useState("");
    const [amount, setAmount] = useState<number | "">("");
    const [taxCodes, setTaxCodes] = useState<{ code: string; name: string; rate: number; type: string }[]>([]);
    const [selectedTaxCodes, setSelectedTaxCodes] = useState<any[]>([]);
    const [providers, setProviders] = useState<{ label: string; value: string }[]>(
        []
    );
    const [selectedProvider, setSelectedProvider] = useState("");
    const [totalAmount, setTotalAmount] = useState<number>(0);

    useEffect(() => {
        const subtotal = Number(amount) || 0;
        const taxTotal = selectedTaxCodes.reduce((acc, tax) => {
            if (tax.type === "percentage") return acc + subtotal * (Number(tax.rate) / 100);
            if (tax.type === "adhoc") return acc + Number(tax.rate);
            return acc;
        }, 0);

        setTotalAmount(subtotal + taxTotal);
    }, [amount, selectedTaxCodes]);


    // Populate form if editing
    useEffect(() => {
        if (initialData) {
            setChargeCode(initialData.charge_code || "");
            setServiceName(initialData.service_name || "");
            setServiceType(initialData.service_type || "");
            setAmount(Number(initialData.amount) || 0);
            setTotalAmount(Number(initialData.total_amount) || 0);
            let initialTaxCodes: any[] = [];
            if (initialData?.tax_codes) {
                try {
                    const parsed = typeof initialData.tax_codes === "string"
                        ? JSON.parse(initialData.tax_codes) // parse the string to array
                        : initialData.tax_codes;

                    initialTaxCodes = parsed.map((t: any) => {
                        // ensure each item is an object
                        return typeof t === "string" ? JSON.parse(t) : t;
                    });

                    console.log("Initial selectedTaxCodes:", initialTaxCodes);
                } catch (err) {
                    console.error("Failed to parse initial tax_codes", err);
                }
            }
            setSelectedTaxCodes(initialTaxCodes);
            setSelectedProvider(initialData.provider_id ? String(initialData.provider_id) : "");
        }
    }, [initialData]);
    console.log("Initial data", initialData);
    // Fetch tax codes and providers
    useEffect(() => {
        if (!open) return;

        const fetchTaxCodes = async () => {
            try {
                const res: any = await apiClient("v1/resource/taxCodes", { method: "GET" });
                const data = res.data;
                if (Array.isArray(data.tax_codes)) {
                    // Store as objects instead of strings
                    const options = data.tax_codes.map((item: { code: string; name: string; rate: number; type: string }) => ({
                        code: item.code,
                        name: item.name,
                        rate: item.rate,
                        type: item.type,
                    }));
                    console.log("Fetched tax codes", options);
                    setTaxCodes(options);
                }
            } catch (err) {
                console.error("Error fetching tax codes", err);
            }
        };

        const fetchProviders = async () => {
            try {
                const res: any = await apiClient("v1/resource/providers/all", { method: "GET" });
                const data = res.data;
                if (Array.isArray(data.providers)) {
                    const options = data.providers.map((p: any) => ({
                        label: `${p.user.first_name} ${p.user.last_name}`,
                        value: String(p.id),
                    }));
                    setProviders(options);
                }
            } catch (err) {
                console.error("Error fetching providers", err);
            }
        };

        fetchTaxCodes();
        fetchProviders();
    }, [open]);


    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        const cleanedTaxCodes = selectedTaxCodes.map((t) =>
            typeof t === "string" ? JSON.parse(t) : t
        );

        const payload: any = {
            charge_code: chargeCode,
            service_name: serviceName,
            service_type: serviceType,
            amount: Number(amount),
            total_amount: Number(totalAmount.toFixed(2)),
            tax_codes: JSON.stringify(cleanedTaxCodes),
            provider_id: Number(selectedProvider),
        };

        if (initialData?.id) {
            payload.id = initialData.id;
        }

        try {
            let response;
            if (initialData?.id) {
                const url = `v1/resource/ChargeCodes/${initialData.id}`;
                console.log("PATCH URL:", url);
                console.log("Payload:", JSON.stringify(payload, null, 2));
                response = await apiClient(url, {
                    method: "PATCH",
                    body: payload,
                });
            } else {
                const url = "v1/resource/ChargeCodes";
                console.log("POST URL:", url);
                console.log("Payload:", JSON.stringify(payload, null, 2));
                response = await apiClient(url, {
                    method: "POST",
                    body: payload,
                });
            }


            console.log("API response", response);
            onSuccess();
            onClose();
        } catch (err) {
            console.error("Error submitting form", err);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle sx={{
                position: "relative",
                textAlign: "center",
                fontWeight: "bold",
                fontSize: "1.25rem"
            }}>
                {initialData ? "Edit Charge Code" : "Add Charge Code"}

                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: "primary.contrastText",
                    }}
                >
                    <X size={20} />
                </IconButton>
            </DialogTitle>


            <DialogContent dividers>
                <Box component="form" onSubmit={handleSubmit} display="flex" flexDirection="column" gap={2}>
                    {/* Wrap two fields per row */}
                    <Box display="flex" gap={2} flexWrap="wrap">
                        <Box flex={1} minWidth={200}>
                            <LabeledTextField
                                label="Charge Code"
                                value={chargeCode}
                                onChange={(val) => setChargeCode(val)}
                                helperText="Unique code for the charge"
                            />
                        </Box>
                        <Box flex={1} minWidth={200}>
                            <LabeledTextField
                                label="Service Name"
                                value={serviceName}
                                onChange={(val) => setServiceName(val)}
                                helperText="Enter a descriptive name for the service. This will be displayed to users."
                            />
                        </Box>
                    </Box>

                    <Box display="flex" gap={2} flexWrap="wrap">
                        <Box flex={1} minWidth={200}>
                            <DropdownField
                                label="Service Type"
                                value={serviceType}
                                onChange={(val) => setServiceType(val)}
                                options={[
                                    { id: "Walk In", name: "Walk In" },
                                    { id: "Home Visit", name: "Home Visit" },
                                    { id: "Tele Consult", name: "Tele Consult" },
                                ]}
                                helperText="Select the type of service (e.g., 'Walk in', 'Home visit', 'Tele Consult')."
                            />


                        </Box>
                        <Box flex={1} minWidth={200}>
                            <LabeledTextField
                                label="Amount"
                                type="number"
                                value={amount}

                                onChange={(val) => setAmount(Number(val))}
                                helperText="Enter the price for this service in your currency (e.g., 500)."
                            />
                        </Box>
                    </Box>

                    <Box display="flex" gap={2} flexWrap="wrap">
                        <Box flex={1} minWidth={200}>
                            <DropdownField
                                label="Provider"
                                value={selectedProvider}
                                options={providers.map((p) => ({ id: p.value, name: p.label }))}
                                onChange={setSelectedProvider}
                                helperText="Select the provider for this charge. Required."
                            />
                        </Box>
                        <Box flex={1} minWidth={200}>
                            <DropdownField
                                label="Tax Codes"
                                options={taxCodes.map((t) => ({
                                    id: JSON.stringify(t),
                                    name: `${t.code} - ${t.name} - ${t.rate}${t.type === "adhoc" ? "₹" : "%"}`,
                                }))}
                                value={selectedTaxCodes.map((t) => JSON.stringify(t))}
                                onChange={(vals: any[]) => {
                                    const parsed = vals.map((v) => (typeof v === "string" ? JSON.parse(v) : v));
                                    setSelectedTaxCodes(parsed);
                                }}
                                multiple
                            />
                        </Box>
                    </Box>
                    <Box display="flex" gap={2} flexWrap="wrap">
                        <LabeledTextField
                            label="Total Amount"
                            type="number"
                            value={totalAmount.toFixed(2)}
                            disabled
                            helperText="Total including selected taxes" onChange={function (val: any): void {
                                throw new Error("Function not implemented.");
                            }} />

                    </Box>
                    <Box display="flex" justifyContent="flex-end" gap={2} mt={2}>
                        <Button variant="outlined" onClick={onClose}>
                            Cancel
                        </Button>
                        <Button type="submit" variant="contained">
                            {initialData ? "Update" : "Submit"}
                        </Button>
                    </Box>
                </Box>
            </DialogContent>
        </Dialog>
    );
};

export default ChargeCodeDialog;
